'use client';

import Card from '@/components/Card/Card';
import classes from '@/components/FormProductCard/FormProductCard.module.scss';
import { IconButton } from '@/components/iconButton';
import collapseClasses from '@/components/swdCollapse/SwdCollapse.module.scss';
import ArrowForwardIosIcon from '@/public/icons/chevron-m-right.svg';
import { Collapsible } from '@base-ui/react/collapsible';
import classNames from 'classnames';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

/** Matches `styles/_breakpoints.scss` `$breakpoint-desktop-min-width`. */
const DESKTOP_LAYOUT_QUERY = '(min-width: 1280px)';

function subscribeToDesktopLayout(onStoreChange: () => void): () => void {
  const mediaQuery = window.matchMedia(DESKTOP_LAYOUT_QUERY);
  mediaQuery.addEventListener('change', onStoreChange);
  return () => mediaQuery.removeEventListener('change', onStoreChange);
}

function useIsDesktopLayout(): boolean {
  return useSyncExternalStore(
    subscribeToDesktopLayout,
    () => window.matchMedia(DESKTOP_LAYOUT_QUERY).matches,
    () => false
  );
}

type FormProductCardProps = {
  title?: string;
  subtitle?: string;
  contentTitle?: string;
  content?: string;
  className?: string;
};

const FormProductCard: React.FC<FormProductCardProps> = (props) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDesktopLayout = useIsDesktopLayout();
  const isOpen = isDesktopLayout || mobileOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isDesktopLayout) {
        setMobileOpen(nextOpen);
      }
    },
    [isDesktopLayout]
  );

  return (
    <Collapsible.Root open={isOpen} onOpenChange={handleOpenChange} role="region" aria-label={t('Produkt')}>
      <Card
        variant="Summary"
        className={classNames(props.className, classes.formProductCard)}
        header={
          <div className={classes.formProductCardHeader}>
            <div className={classes.formProductCardHeaderWrapper}>
              <div className={classes.formProductCardSubTitle}>{props.subtitle}</div>
              <h3 className={classes.formProductCardTitle}>{props.title}</h3>
            </div>
            {!isDesktopLayout ? (
              <Collapsible.Trigger
                render={({ color: _triggerColor, ...triggerProps }) => (
                  <IconButton
                    {...triggerProps}
                    aria-label={mobileOpen ? t('Inhalt zuklappen') : t('Inhalt erweitern')}
                    className={classNames(
                      classes.formProductCardToggleContent,
                      {
                        [classes.open as string]: mobileOpen,
                      },
                      'dark'
                    )}
                    size="small"
                  >
                    <ArrowForwardIosIcon aria-hidden="true" />
                  </IconButton>
                )}
              />
            ) : null}
          </div>
        }
      >
        <div className={classes.formProductCardCollapse}>
          <Collapsible.Panel keepMounted className={collapseClasses.collapsiblePanel}>
            {props.contentTitle?.trim() ? (
              <p className={classes.formProductCardContentTitle}>{props.contentTitle}</p>
            ) : null}

            <ul className={classNames(classes.formProductCardContent, 'bulletpoints', 'bulletpoints--success')}>
              {props.content?.split(/\r?\n/).map((element: string, index: number) => (
                <li key={`list-${index}`}>{element}</li>
              ))}
            </ul>
          </Collapsible.Panel>
        </div>
      </Card>
    </Collapsible.Root>
  );
};

export default FormProductCard;
